import React, { useEffect, useState } from 'react';
import { FiCheck, FiChevronDown, FiChevronRight, FiFileText, FiFolder, FiLayers, FiX } from 'react-icons/fi';
import {
  acceptProjectInvitation,
  declineProjectInvitation,
  getProjectInvitationPreview,
} from '../utils/conversationApi';
import { projectFolderName } from '../utils/projectSharing';
import './ProjectInvitationModal.css';

// The prompt states access in a few plain words under the project's name,
// the way Claude ("Can edit") and ChatGPT ("edit access") do; what that
// covers is the line's hover text, not a paragraph competing with the project.
const ROLE_INFO = {
  view: {
    label: 'View access',
    word: 'view',
    details: "You can chat, and see the instructions and files, but not change them or invite people. Your conversations stay private.",
  },
  edit: {
    label: 'Edit access',
    word: 'edit',
    details: 'You can chat, change the instructions and files, and invite people. Your conversations stay private.',
  },
};

const initialsOf = (invite) => {
  const source = (invite.invited_by_name || invite.invited_by_email || '').trim();
  const words = source.split(/[\s@.]+/).filter(Boolean);
  if (words.length === 0) return '?';
  return (words.length === 1 ? words[0].slice(0, 2) : words[0][0] + words[1][0]).toUpperCase();
};

/** The inviter's photo, or their initials when there is none (or it fails to load). */
function InviterAvatar({ invite, small = false }) {
  const [failed, setFailed] = useState(false);
  const sizeClass = small ? ' pim-avatar--small' : '';
  const img = typeof invite.invited_by_profile_img === 'string' ? invite.invited_by_profile_img.trim() : '';
  if (img && img.toUpperCase() !== 'N/A' && !failed) {
    return (
      <img
        src={img}
        alt=""
        referrerPolicy="no-referrer"
        className={`pim-avatar${sizeClass}`}
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <span className={`pim-avatar pim-avatar--initials${sizeClass}`} aria-hidden="true">
      {initialsOf(invite)}
    </span>
  );
}

/**
 * One invitation as a card at the top of the Projects menu -- the entry point
 * to the Accept / Decline prompt. Reviewing it does not answer it: it just
 * opens ProjectInvitationModal on that invitation.
 */
export function InvitationCard({ invitation, onReview }) {
  const inviterName = invitation.invited_by_name || invitation.invited_by_email || 'A colleague';
  const role = ROLE_INFO[invitation.role] || ROLE_INFO.view;
  return (
    <div className="pim-card">
      <InviterAvatar invite={invitation} small />
      <div className="pim-card-text">
        <span className="pim-card-title">
          <strong>{inviterName}</strong> invited you
        </span>
        <span className="pim-card-project" title={invitation.project_name}>{invitation.project_name}</span>
        <span className="pim-card-role">{role.label}</span>
      </div>
      <button type="button" className="pim-card-review" onClick={() => onReview(invitation.project_id)}>
        Review
      </button>
    </div>
  );
}

/**
 * "What's in this project?" for someone who has not accepted yet. Fetched the
 * first time it is expanded, not up front -- most people will not open it.
 * Names only (the server sends nothing else): file contents, previews and
 * search all stay behind accepting. `onGone` fires if the invitation turns
 * out to be cancelled meanwhile, so the whole prompt can say so.
 */
function InvitationPreview({ projectId, onGone }) {
  const [expanded, setExpanded] = useState(false);
  const [state, setState] = useState({ status: 'idle', data: null, error: '' });

  const load = async () => {
    setState({ status: 'loading', data: null, error: '' });
    try {
      const data = await getProjectInvitationPreview(projectId);
      setState({ status: 'ready', data, error: '' });
    } catch (err) {
      if (onGone && /no pending invitation/i.test(err?.message || '')) {
        onGone();
        return;
      }
      setState({ status: 'error', data: null, error: err.message || 'Could not load the preview.' });
    }
  };

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && (state.status === 'idle' || state.status === 'error')) load();
  };

  const { data } = state;
  return (
    <div className="pim-preview">
      <button
        type="button"
        className="pim-preview-toggle"
        onClick={toggle}
        aria-expanded={expanded}
      >
        <span className="pim-preview-toggle-icon" aria-hidden="true"><FiLayers size={16} /></span>
        <span className="pim-preview-toggle-text">
          <span className="pim-preview-toggle-title">Preview project</span>
          <span className="pim-preview-toggle-sub">See its instructions and files before you join</span>
        </span>
        {expanded
          ? <FiChevronDown size={16} className="pim-preview-toggle-chevron" aria-hidden="true" />
          : <FiChevronRight size={16} className="pim-preview-toggle-chevron" aria-hidden="true" />}
      </button>

      {expanded && (
        <div className="pim-preview-body">
          {state.status === 'loading' && <p className="pim-preview-muted">Loading…</p>}
          {state.status === 'error' && (
            <p className="pim-preview-muted" role="alert">
              {state.error}{' '}
              <button type="button" className="pim-preview-retry" onClick={load}>Try again</button>
            </p>
          )}
          {state.status === 'ready' && data && (
            <>
              <h3 className="pim-preview-heading">Instructions</h3>
              {data.instructions
                ? <div className="pim-preview-instructions">{data.instructions}</div>
                : <p className="pim-preview-muted">No instructions yet.</p>}

              <h3 className="pim-preview-heading">
                Knowledge base
                <span className="pim-preview-count">
                  {data.file_count} {data.file_count === 1 ? 'file' : 'files'}
                  {data.folders.length > 0 && ` · ${data.folders.length} ${data.folders.length === 1 ? 'folder' : 'folders'}`}
                </span>
              </h3>
              {data.files.length === 0 ? (
                <p className="pim-preview-muted">No files yet.</p>
              ) : (
                <ul className="pim-preview-files">
                  {data.files.map((f, i) => (
                    // Names can repeat across folders, so the position is part of the key.
                    <li key={`${f.folder_name || ''}/${f.name}/${i}`} className="pim-preview-file">
                      <FiFileText size={13} aria-hidden="true" />
                      <span className="pim-preview-file-name" title={f.name}>{f.name}</span>
                      {f.folder_name && (
                        <span className="pim-preview-file-folder"><FiFolder size={11} aria-hidden="true" /> {f.folder_name}</span>
                      )}
                    </li>
                  ))}
                  {data.file_count > data.files.length && (
                    <li className="pim-preview-more">…and {data.file_count - data.files.length} more</li>
                  )}
                </ul>
              )}
              <p className="pim-preview-note">
                You&apos;re only seeing names. File contents open up once you accept.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Accept / decline prompt for project invitations. It never opens by itself at
 * sign-in: the Projects icon pulses, and the invitation cards in the Projects
 * menu (InvitationCard) open it. The invite email's link opens it directly,
 * since clicking that is an explicit "show me". An invitation grants nothing
 * until accepted: the project is not in the invitee's Projects list and 404s
 * if opened, so this prompt is the only way in.
 *
 * - Accept: the project joins their "Shared with me" list and opens (via
 *   `onAccepted`).
 * - Decline: the invite is removed and the project never appears for them;
 *   the owner can invite them again later. Asks to confirm first.
 * - Not now (button / X / Esc): leaves it pending -- the Projects icon keeps
 *   its badge until it is accepted or declined.
 * - Preview project: shows what is in it (names and instructions only) so
 *   they can decide.
 *
 * Controlled: App.js owns the invitation list (it also drives the Projects
 * icon's badge and the menu's cards), so this renders one invitation while
 * `open` -- the one named by `focusProjectId`, else the newest -- and reports
 * back through the callbacks:
 *   onClose()            -- not now (button / X / Esc)
 *   onAnswered(projectId) -- accepted, declined, or found to be gone
 *   onAccepted(project)   -- additionally, on a successful accept
 */
export default function ProjectInvitationModal({
  invitations, open, focusProjectId = null, onClose, onAnswered, onAccepted,
}) {
  const [busy, setBusy] = useState(null); // 'accept' | 'decline' | null
  const [confirmingDecline, setConfirmingDecline] = useState(false);
  const [error, setError] = useState('');
  // For someone returning to a project they left: whether to bring their old
  // conversations from it back. On by default -- it is shown right next to
  // the Accept button, and leaving again just saves them to the folder again.
  const [restoreConversations, setRestoreConversations] = useState(true);
  // The server said there is nothing pending any more (cancelled, or already
  // answered in another window): the prompt says so, and closing it drops the
  // invitation.
  const [gone, setGone] = useState(false);

  const current = invitations.find((i) => i.project_id === focusProjectId) || invitations[0];
  const visible = Boolean(open && current);

  // A reopened prompt (or the next invitation) starts clean.
  useEffect(() => {
    setConfirmingDecline(false);
    setError('');
    setRestoreConversations(true);
    setGone(false);
  }, [visible, current?.project_id]);

  useEffect(() => {
    if (!visible) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape' || busy) return;
      if (gone) onAnswered(current.project_id);
      else onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [visible, busy, gone, current, onClose, onAnswered]);

  if (!visible) return null;

  const role = ROLE_INFO[current.role] || ROLE_INFO.view;
  const inviterName = current.invited_by_name || current.invited_by_email || 'A colleague';
  const savedCount = current.saved_conversation_count || 0;
  // Where those conversations wait: the folder leaving or being removed
  // saved them to.
  const restoreFolderName = projectFolderName({ name: current.project_name });

  const removeCurrent = () => {
    onAnswered(current.project_id);
    setConfirmingDecline(false);
    setError('');
    setGone(false);
  };

  // The server says "No pending invitation" when it was cancelled (or already
  // answered elsewhere) -- nothing left to act on. Say so rather than closing
  // silently, and offer only a way out.
  const isGone = (err) => /no pending invitation/i.test(err?.message || '');
  const markGone = () => {
    setConfirmingDecline(false);
    setError('');
    setGone(true);
  };

  const handleAccept = async () => {
    setBusy('accept');
    setError('');
    try {
      const project = await acceptProjectInvitation(current.project_id, {
        restoreConversations: savedCount > 0 && restoreConversations,
      });
      removeCurrent();
      if (onAccepted) onAccepted(project);
    } catch (err) {
      if (isGone(err)) {
        markGone();
      } else {
        setError(err.message || 'Could not accept this invitation. Please try again.');
      }
    } finally {
      setBusy(null);
    }
  };

  const handleDecline = async () => {
    setBusy('decline');
    setError('');
    try {
      await declineProjectInvitation(current.project_id);
      removeCurrent();
    } catch (err) {
      if (isGone(err)) {
        markGone();
      } else {
        setError(err.message || 'Could not decline this invitation. Please try again.');
      }
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="pim-overlay">
      <div
        className="pim-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pim-title"
        // One card per invitation, so the entrance animation replays for each
        // -- and the preview's own state (expanded / loaded) starts fresh.
        key={current.project_id}
      >
        <button
          type="button"
          className="pim-close"
          onClick={gone ? removeCurrent : onClose}
          disabled={Boolean(busy)}
          aria-label={gone ? 'Close' : 'Not now'}
          title={gone ? 'Close' : 'Not now'}
        >
          <FiX size={18} />
        </button>

        <div className="pim-body">
          <InviterAvatar invite={current} />
          <p className="pim-eyebrow">
            <strong>{inviterName}</strong> invited you to join a project
          </p>
          <h2 id="pim-title" className="pim-project-name" title={current.project_name}>
            {current.project_name}
          </h2>
          {!gone && (
            <p className="pim-access" title={role.details}>
              You&apos;ll have <strong>{role.word} access</strong> to this project
            </p>
          )}

          {gone ? (
            <p className="pim-confirm" role="alert">
              This invitation is no longer available. It may have been cancelled, or already
              answered in another window. Ask {current.invited_by_name || 'whoever invited you'} to
              invite you again if you still need access.
            </p>
          ) : (
            <InvitationPreview projectId={current.project_id} onGone={markGone} />
          )}

          {/* Only for someone who was in this project before: their
              conversations from it were saved to a folder when they left or
              were removed. */}
          {!gone && savedCount > 0 && (
            <label className="pim-restore">
              <input
                type="checkbox"
                checked={restoreConversations}
                onChange={(e) => setRestoreConversations(e.target.checked)}
                disabled={Boolean(busy)}
              />
              <span>
                <strong>
                  Bring back my {savedCount} {savedCount === 1 ? 'conversation' : 'conversations'}
                </strong>
                <span className="pim-restore-desc">
                  When your access to this project ended, your {savedCount === 1 ? 'conversation' : 'conversations'}
                  {' '}from it {savedCount === 1 ? 'was' : 'were'} saved to the{' '}
                  <strong>{restoreFolderName}</strong> folder in your Conversations. Keep this ticked to move
                  {' '}{savedCount === 1 ? 'it' : 'them'} back into this project, or untick to leave
                  {' '}{savedCount === 1 ? 'it' : 'them'} in the regular conversations itself and start fresh here.
                </span>
              </span>
            </label>
          )}

          {confirmingDecline && (
            <p className="pim-confirm" role="alert">
              Decline this invitation? It will be removed and you won&apos;t see this project
              unless {current.invited_by_name || 'they'} invite{current.invited_by_name ? 's' : ''} you again.
            </p>
          )}
          {error && <p className="pim-error" role="alert">{error}</p>}
        </div>

        <div className="pim-footer">
          {!gone && invitations.length > 1 && (
            <span className="pim-counter">{invitations.length} pending invitations</span>
          )}
          {gone ? (
            <button
              type="button"
              className="pim-btn pim-btn--primary"
              onClick={removeCurrent}
              autoFocus
            >
              OK
            </button>
          ) : confirmingDecline ? (
            <>
              <button
                type="button"
                className="pim-btn pim-btn--ghost"
                onClick={() => { setConfirmingDecline(false); setError(''); }}
                disabled={Boolean(busy)}
              >
                Go back
              </button>
              <button
                type="button"
                className="pim-btn pim-btn--danger"
                onClick={handleDecline}
                disabled={Boolean(busy)}
              >
                {busy === 'decline' ? 'Declining...' : 'Yes, decline'}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="pim-btn pim-btn--text"
                onClick={() => { setConfirmingDecline(true); setError(''); }}
                disabled={Boolean(busy)}
              >
                Decline
              </button>
              <button
                type="button"
                className="pim-btn pim-btn--ghost"
                onClick={onClose}
                disabled={Boolean(busy)}
                title="Keep this invitation and answer later"
              >
                Not now
              </button>
              <button
                type="button"
                className="pim-btn pim-btn--primary"
                onClick={handleAccept}
                disabled={Boolean(busy)}
                autoFocus
              >
                <FiCheck size={14} /> {busy === 'accept' ? 'Joining...' : 'Accept'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
